<!-- static/src/components/ui/OnboardingWizard.vue -->
<!-- Guided onboarding modal — shown once after first login -->
<template>
  <!-- Client-only: gated behind `mounted` so this <Teleport> is absent from
       BOTH the SSR snapshot and the client's first hydration render, making
       the two agree exactly. Without this, the SSR pass emitted teleport
       anchors (plus its out-of-band body buffer) that Vue's client teleport
       hydration couldn't reconcile while the modal is closed, producing a
       spurious "Hydration node mismatch ... expected on client: Comment".
       The component is always mounted by App.vue (driven by :visible), so the
       gate — not a v-if on :visible — is what keeps the open/close transition
       animating after hydration. -->
  <Teleport v-if="mounted" to="body">
    <Transition name="modal">
      <div v-if="visible" class="fixed inset-0 z-50 flex items-center justify-center">
        <!-- Backdrop -->
        <div class="absolute inset-0 bg-ink-900/60 backdrop-blur-sm" @click="dismiss" />

        <!-- Card -->
        <div class="relative bg-white rounded-2xl shadow-hero border border-ink-100 w-full max-w-lg mx-4 overflow-hidden">
          <!-- Progress bar -->
          <div class="h-1 bg-ink-100">
            <div class="h-full bg-brand-500 transition-all duration-500 ease-out" :style="{ width: progressPct + '%' }" />
          </div>

          <!-- Step content -->
          <div class="p-8">
            <Transition name="step" mode="out-in">
              <!-- Step 0: Welcome -->
              <div v-if="step === 0" :key="0" class="text-center">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brand-100 text-brand-600 flex items-center justify-center">
                  <Icon name="search" :size="28" />
                </div>
                <h2 class="font-display text-2xl text-ink-900 mb-2">Welcome to LeadRanks</h2>
                <p class="text-ink-600 text-sm mb-6 leading-relaxed">
                  The SEO intelligence platform that turns audits into leads. Let's get you started with the 3 things you'll do most.
                </p>

                <div class="space-y-3 text-left mb-6">
                  <div class="flex gap-3 p-3 rounded-xl bg-brand-50/60 border border-brand-100">
                    <span class="w-8 h-8 rounded-lg bg-brand-100 text-brand-700 flex items-center justify-center shrink-0 mt-0.5 text-sm font-bold">1</span>
                    <div>
                      <div class="font-semibold text-ink-900 text-sm">Run SEO audits</div>
                      <div class="text-xs text-ink-600 mt-0.5">Get 100+ point analysis with actionable fixes in minutes</div>
                    </div>
                  </div>
                  <div class="flex gap-3 p-3 rounded-xl bg-green-50/60 border border-green-100">
                    <span class="w-8 h-8 rounded-lg bg-green-100 text-green-700 flex items-center justify-center shrink-0 mt-0.5 text-sm font-bold">2</span>
                    <div>
                      <div class="font-semibold text-ink-900 text-sm">Find qualified leads</div>
                      <div class="text-xs text-ink-600 mt-0.5">AI-powered prospecting scores websites by revenue potential</div>
                    </div>
                  </div>
                  <div class="flex gap-3 p-3 rounded-xl bg-purple-50/60 border border-purple-100">
                    <span class="w-8 h-8 rounded-lg bg-purple-100 text-purple-700 flex items-center justify-center shrink-0 mt-0.5 text-sm font-bold">3</span>
                    <div>
                      <div class="font-semibold text-ink-900 text-sm">Track and improve</div>
                      <div class="text-xs text-ink-600 mt-0.5">Monitor progress, compare competitors, close more deals</div>
                    </div>
                  </div>
                </div>

                <div class="bg-ink-50 rounded-xl p-4 text-center mb-6">
                  <div class="text-xs text-ink-500 mb-1">Your plan</div>
                  <div class="font-semibold text-ink-900">{{ tierDisplayName }}</div>
                  <div class="text-xs text-ink-600 mt-0.5">
                    {{ tierReportsText }}
                  </div>
                </div>
              </div>

              <!-- Step 1: Quick action — Run your first audit -->
              <div v-else-if="step === 1" :key="1" class="text-center">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-brand-100 text-brand-600 flex items-center justify-center">
                  <Icon name="search" :size="28" />
                </div>
                <h2 class="font-display text-2xl text-ink-900 mb-2">Run your first audit</h2>
                <p class="text-ink-600 text-sm mb-6 leading-relaxed">
                  Enter any URL below and we'll analyze it in 2–3 minutes. You'll get a full report with prioritized fixes.
                </p>

                <div class="flex gap-2 mb-6">
                  <input
                    v-model="quickUrl"
                    type="url"
                    placeholder="https://example.com"
                    class="flex-1 input"
                    @keyup.enter="startAudit"
                  />
                  <button class="btn-brand" @click="startAudit" :disabled="!quickUrl.trim()">
                    Audit <Icon name="arrow-right" :size="14" />
                  </button>
                </div>
                <p class="text-2xs text-ink-500">No credit card needed · Free forever plan available</p>
              </div>

              <!-- Step 2: Find leads -->
              <div v-else-if="step === 2" :key="2" class="text-center">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center">
                  <Icon name="users" :size="28" />
                </div>
                <h2 class="font-display text-2xl text-ink-900 mb-2">Find qualified leads</h2>
                <p class="text-ink-600 text-sm mb-6 leading-relaxed">
                  Our AI scans millions of websites and scores them by SEO health — so you know exactly who needs your help.
                </p>

                <div class="grid grid-cols-3 gap-3 mb-6">
                  <div class="p-3 rounded-xl bg-ink-50 text-center">
                    <div class="font-bold text-ink-900 text-lg">100+</div>
                    <div class="text-2xs text-ink-500">leads per search</div>
                  </div>
                  <div class="p-3 rounded-xl bg-ink-50 text-center">
                    <div class="font-bold text-ink-900 text-lg">0–10</div>
                    <div class="text-2xs text-ink-500">lead scoring</div>
                  </div>
                  <div class="p-3 rounded-xl bg-ink-50 text-center">
                    <div class="font-bold text-ink-900 text-lg">CSV</div>
                    <div class="text-2xs text-ink-500">export ready</div>
                  </div>
                </div>
              </div>

              <!-- Step 3: All set -->
              <div v-else-if="step === 3" :key="3" class="text-center">
                <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-green-100 text-green-600 flex items-center justify-center">
                  <svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 6 9 17l-5-5"/>
                  </svg>
                </div>
                <h2 class="font-display text-2xl text-ink-900 mb-2">You're all set!</h2>
                <p class="text-ink-600 text-sm mb-6 leading-relaxed">
                  Head to your dashboard to start exploring. We've highlighted the key actions for you.
                </p>
              </div>
            </Transition>
          </div>

          <!-- Footer -->
          <div class="px-8 pb-6 flex items-center justify-between">
            <!-- Step dots -->
            <div class="flex gap-1.5">
              <button
                v-for="i in 4" :key="i"
                @click="step = i - 1"
                class="w-2 h-2 rounded-full transition-all"
                :class="step === i - 1 ? 'bg-brand-600 w-4' : 'bg-ink-200 hover:bg-ink-300'"
              />
            </div>

            <div class="flex gap-2">
              <button v-if="step > 0" class="btn-ghost btn-sm" @click="step--">Back</button>
              <button v-if="step === 0" class="btn-ghost btn-sm text-ink-500" @click="dismiss">Skip tour</button>
              <button
                v-if="step < 3"
                class="btn-brand btn-sm"
                @click="step++"
              >
                {{ step === 0 ? 'Get started' : 'Next' }}
                <Icon name="arrow-right" :size="14" />
              </button>
              <button
                v-else
                class="btn-brand btn-sm"
                @click="finish"
              >
                Go to dashboard <Icon name="arrow-right" :size="14" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import Icon from '@/components/ui/Icon.vue';

const router = useRouter();

// See the template comment: keeps the <Teleport> out of the SSR snapshot and
// the first hydration render (onMounted never runs during SSR).
const mounted = ref(false);
onMounted(() => {
  mounted.value = true;
});

const props = defineProps({
  tier: { type: String, default: 'starter' },
  reportsRemaining: { type: Number, default: 5 },
  visible: { type: Boolean, default: false },
});

const emit = defineEmits(['complete', 'dismiss']);

const step = ref(0);
const quickUrl = ref('');

const progressPct = computed(() => ((step.value + 1) / 4) * 100);

const tierDisplayName = computed(() => {
  const names = { starter: 'Starter', free: 'Starter', professional: 'Pro', pro: 'Pro', growth: 'Pro', agency: 'Agency', scale: 'Agency', business: 'Agency', enterprise: 'Enterprise' };
  return names[props.tier] || 'Starter';
});

const tierReportsText = computed(() => {
  if (['growth', 'scale', 'enterprise', 'pro', 'business', 'agency'].includes(props.tier)) {
    return 'Unlimited SEO audits + AI Intelligence Copilot';
  }
  return `${props.reportsRemaining} free SEO audits per month`;
});

const startAudit = () => {
  if (!quickUrl.value.trim()) return;
  const encoded = encodeURIComponent(quickUrl.value.trim());
  dismiss();
  router.push(`/analyze?url=${encoded}&mode=standard`);
};

const dismiss = () => {
  emit('dismiss');
};

const finish = () => {
  emit('complete');
};

defineExpose({ step });
</script>

<style scoped>
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
}
.step-enter-active, .step-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.step-enter-from {
  opacity: 0;
  transform: translateX(20px);
}
.step-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}
</style>
